import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createGlobalMaterial,
  createGlobalTool,
  searchGlobalMaterials,
  searchGlobalTools,
} from '@/api/global-catalog'
import type {
  CreateGlobalCatalogMaterialRequest,
  CreateGlobalCatalogToolRequest,
  GlobalCatalogKind,
  GlobalCatalogMaterial,
  GlobalCatalogPage,
  GlobalCatalogTool,
} from '@/api/types'

const DEBOUNCE_MS = 300
const FIVE_MINUTES = 5 * 60 * 1000

const useDebounced = <T>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(id)
  }, [value, delay])
  return debounced
}

/**
 * Phase 15 — debounced global-catalog search. Kind-discriminated to keep the picker UX clear:
 * material results never mix with tool results. Empty `query` returns recency-sorted server
 * results (the picker tab opens to "what's new" before the user types).
 */
export const useGlobalCatalogSearch = <K extends GlobalCatalogKind>(
  kind: K,
  query: string,
  enabled: boolean = true,
  page: number = 0,
  size: number = 20
) => {
  const debouncedQuery = useDebounced(query, DEBOUNCE_MS)
  return useQuery<
    GlobalCatalogPage<K extends 'MATERIAL' ? GlobalCatalogMaterial : GlobalCatalogTool>
  >({
    queryKey: ['globalCatalog', kind, debouncedQuery, page, size],
    queryFn: async () => {
      if (kind === 'MATERIAL') {
        return (await searchGlobalMaterials(debouncedQuery, page, size)) as GlobalCatalogPage<
          K extends 'MATERIAL' ? GlobalCatalogMaterial : GlobalCatalogTool
        >
      }
      return (await searchGlobalTools(debouncedQuery, page, size)) as GlobalCatalogPage<
        K extends 'MATERIAL' ? GlobalCatalogMaterial : GlobalCatalogTool
      >
    },
    enabled,
    staleTime: FIVE_MINUTES,
  })
}

export const useCreateGlobalMaterial = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateGlobalCatalogMaterialRequest) => createGlobalMaterial(request),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['globalCatalog', 'MATERIAL'] }),
  })
}

export const useCreateGlobalTool = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateGlobalCatalogToolRequest) => createGlobalTool(request),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['globalCatalog', 'TOOL'] }),
  })
}
